import React from 'react'
import { inject, observer } from 'mobx-react'
import { Layout, Table, Modal, Tag, message, Button } from 'antd'
import moment from 'moment'
import benefitsService from '@services/benefits'
import mediwhoService from '@services/mediwho-app'
import BenefitModal from './BenefitModal'
import { BENEFIT_TYPES, ACTION_TYPES } from './benefitConstants'
import NotificationModal from '../Notifications/NotificationModal'
import {
    mediwhoAppTableWrapStyle,
    centeredBodyCell,
    centeredHeaderCell,
    MediwhoAppPageHeader,
    renderTruncatedValue,
    renderActionButtons,
    rtlLayoutStyle,
    confirmDelete,
    TargetPopoverCell
} from '../shared/mediwhoAppStyles'

const TARGET_META: Record<string, { label: string; color: string }> = {
    all:    { label: 'כולם',           color: 'geekblue' },
    ids:    { label: 'מזהי משתמש',    color: 'purple' },
    phones: { label: 'טלפונים',       color: 'cyan' },
    emails: { label: 'אימיילים',      color: 'orange' }
}

const PARTNER_URL = 'mediwho.boomclub.org'

@inject((store: any) => {
    const { authStore } = store
    return { authStore }
})
@observer
class BenefitsPage extends React.Component<any, any> {
    state = {
        data: [],
        total: 0,
        activeTotal: 0,
        inactiveTotal: 0,
        allRows: [] as any[],
        page: 1,
        loading: false,
        modalOpen: false,
        selected: null as any,
        notifModalOpen: false,
        notifSaving: false,
        notifPrefill: null as any,
        pendingBenefit: null as any,
        orderDraft: null as any[] | null,
        reorderSaving: false,
        reorderMode: false
    }

    isPartner = () => {
        const roles = this.props.authStore.roles
        return roles.indexOf('benefit_partner') !== -1 && roles.indexOf('admin') === -1
    }

    isOwn = (row: any) => {
        if (!this.isPartner()) return true
        if (String(row.action_value || '').indexOf(PARTNER_URL) !== -1) return true
        return row.created_by === this.props.authStore.id
    }

    dragIndex: number = -1

    componentDidMount() {
        this.load(1)
    }

    sortBenefits = (list: any[]): any[] => list.slice().sort((a: any, b: any) => {
        if (b.is_active !== a.is_active) return b.is_active - a.is_active
        if (a.sort_order && b.sort_order) return a.sort_order - b.sort_order
        return b.id - a.id
    })

    fetchAllSorted = async (): Promise<any[]> => {
        const allRes = await benefitsService.list({ page: 1, limit: 9999 })
        return this.sortBenefits(allRes.data.data || [])
    }

    load = async (page: number) => {
        this.setState({ loading: true })
        try {
            const [res, allData] = await Promise.all([
                benefitsService.list({ page }),
                this.fetchAllSorted()
            ])
            const { data, total } = res.data
            const sorted = this.sortBenefits(data as any[])
            const activeTotal = allData.filter((r: any) => r.is_active).length
            this.setState({ data: sorted, total, page, orderDraft: null, activeTotal, inactiveTotal: allData.length - activeTotal, allRows: allData })
        } catch (e) {
            message.error('שגיאה בטעינת ההטבות')
        } finally {
            this.setState({ loading: false })
        }
    }

    swapRows = (list: any[], from: number, to: number): any[] | null => {
        if (to < 0 || to >= list.length) return null
        if (list[from].is_active !== list[to].is_active) return null
        // a partner may not move a row they do not own, or move over one
        if (!this.isOwn(list[from]) || !this.isOwn(list[to])) return null
        const result = list.slice()
        const tmp = result[from]
        result[from] = result[to]
        result[to] = tmp
        return result
    }

    moveRow = (index: number, dir: -1 | 1) => {
        const list: any[] = this.state.orderDraft || this.state.data
        const next = this.swapRows(list, index, index + dir)
        if (next) this.setState({ orderDraft: next })
    }

    saveOrder = async () => {
        const draft: any[] = this.state.orderDraft || []
        this.setState({ reorderSaving: true })
        try {
            const allData = await this.fetchAllSorted()

            // The draft holds the user's new order for the current page rows.
            // Replace those slots in allData with the draft's new order.
            const currentPageIds = new Set(draft.map((r: any) => r.id))
            const slots: number[] = []
            allData.forEach((r: any, i: number) => { if (currentPageIds.has(r.id)) slots.push(i) })

            // Place draft rows into the slots in the user's new order
            slots.forEach((slotIdx: number, draftPos: number) => {
                allData[slotIdx] = draft[draftPos]
            })

            // Assign global sort_order: active group 1..N, inactive group N+1..total
            let items = allData.map((row: any, i: number) => ({ id: row.id, sort_order: i + 1 }))
            // a partner may only send sort_order updates for rows they own
            if (this.isPartner()) {
                items = items.filter((it: any) => this.isOwn(allData.find((r: any) => r.id === it.id)))
            }

            await benefitsService.reorder(items)
            message.success('סדר ההטבות נשמר')
            this.setState({ reorderMode: false, orderDraft: null })
            this.load(this.state.page)
        } catch (e) {
            message.error('שגיאה בשמירת הסדר')
        } finally {
            this.setState({ reorderSaving: false })
        }
    }

    deleteBenefit = (id: number) => {
        confirmDelete('מחיקת הטבה', 'האם אתה בטוח שברצונך למחוק את ההטבה?', async () => {
            try {
                await benefitsService.delete(id)
                message.success('ההטבה נמחקה')
                this.load(this.state.page)
            } catch (e) {
                console.error(e)
                Modal.error({ title: 'שגיאה', content: 'שגיאה במחיקת ההטבה' })
            }
        })
    }

    openAdd = () => this.setState({ modalOpen: true, selected: null })
    openEdit = (row: any) => this.setState({ modalOpen: true, selected: row })
    closeModal = () => this.setState({ modalOpen: false, selected: null })

    onSaved = async (triggerNotification: boolean, benefitData: { title: string, description: string }, body: any): Promise<boolean> => {
        const isNew = !(this.state.selected && this.state.selected.id)

        try {
            if (isNew) {
                await benefitsService.create(body)
            } else {
                await benefitsService.update(this.state.selected.id, body)
            }
        } catch (e) {
            message.error('שגיאה בשמירת ההטבה')
            return false
        }

        this.closeModal()
        if (triggerNotification) {
            const prefill = { title: 'הטבה חדשה: ' + benefitData.title + '!', body: benefitData.description || '', action: 'open_benefits' }
            this.setState({ notifModalOpen: true, pendingBenefit: null, notifPrefill: prefill })
        } else {
            message.success(isNew ? 'ההטבה נוצרה' : 'ההטבה עודכנה')
        }
        await this.load(this.state.page)
        return true
    }

    handleNotifSubmit = async (payload: any) => {
        this.setState({ notifSaving: true })
        try {
            if (this.state.pendingBenefit) {
                await benefitsService.create(this.state.pendingBenefit)
            }
            await mediwhoService.createNotification(payload)
            message.success('ההתראה נשלחה')
            this.setState({ notifModalOpen: false, pendingBenefit: null })
            this.load(this.state.page)
        } catch (e) {
            message.error('שגיאה בשליחת ההתראה')
        } finally {
            this.setState({ notifSaving: false })
        }
    }

    columns = () => [
        {
            title: 'כותרת ראשית',
            dataIndex: 'title',
            width: '13%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => renderTruncatedValue(v, 20)
        },
        {
            title: 'תיאור מלא',
            dataIndex: 'description',
            width: '18%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => renderTruncatedValue(v, 40)
        },
        {
            title: 'סוג הטבה',
            dataIndex: 'benefit_type',
            width: '10%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => {
                const item = BENEFIT_TYPES.find(t => t.value === v) || { label: v, color: 'default', icon: '🏷️' }
                return <Tag color={item.color}>{item.icon} {item.label}</Tag>
            }
        },
        {
            title: 'סוג פעולה',
            dataIndex: 'action_type',
            width: '9%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => {
                const item = ACTION_TYPES.find(t => t.value === v)
                return item ? item.label : v
            }
        },
        {
            title: 'ערך פעולה',
            dataIndex: 'action_value',
            width: '12%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => renderTruncatedValue(v, 20)
        },
        {
            title: 'קהל יעד',
            key: 'target_type',
            width: '9%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (_: any, row: any) => <TargetPopoverCell targetType={row.target_type} targetValue={row.target_value} targetMeta={TARGET_META} />
        },
        {
            title: 'תאריך תפוגה',
            dataIndex: 'expiration_date',
            width: '11%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => v ? moment(v).format('DD/MM/YYYY') : '—'
        },
        {
            title: 'בתוקף',
            dataIndex: 'is_active',
            width: '7%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: number) => <Tag color={v ? 'green' : 'red'} style={{minWidth: 25, textAlign: 'center'}}>{v ? 'פעיל' : 'לא פעיל'}</Tag>
        },
        {
            title: 'סדר ההטבות באפליקציה',
            key: 'sort_order',
            width: '6%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (_: any, __: any, index: number) => (this.state.page - 1) * 10 + index + 1
        },
        ...(this.state.reorderMode ? [{
            title: '',
            key: 'drag',
            width: '5%',
            onCell: () => ({ style: { verticalAlign: 'middle' as const, textAlign: 'center' as const, cursor: 'grab', color: '#bbb', fontSize: 18 } }),
            render: () => '⠿'
        }] : []),
        {
            title: '',
            key: 'action',
            width: '10%',
            onCell: () => ({ style: { verticalAlign: 'middle' as const } }),
            render: (_: any, row: any) => renderActionButtons({
                onEdit: () => this.openEdit(row),
                onDelete: () => this.deleteBenefit(row.id),
                disabled: !this.isOwn(row)
            })
        }
    ]

    render() {
        const { data, total, activeTotal, inactiveTotal, page, loading, modalOpen, selected, notifModalOpen, notifSaving, orderDraft, reorderSaving, reorderMode } = this.state
        const displayData: any[] = orderDraft || data
        const subtitle = activeTotal === 1 ? 'הטבה אחת פעילה' : activeTotal + ' הטבות פעילות'
        return (
            <Layout style={rtlLayoutStyle}>
                <MediwhoAppPageHeader
                    title="הטבות"
                    subtitle={subtitle}
                    actionLabel="+ הטבה חדשה"
                    onAction={this.openAdd}
                    extraActions={
                        <Button
                            icon={reorderMode ? 'close' : 'ordered-list'}
                            type="dashed"
                            className="mediwho-benefits-reorder-toggle-btn"
                            style={{ borderColor: '#722ed1', color: '#722ed1' }}
                            onClick={() => this.setState({ reorderMode: !reorderMode, orderDraft: reorderMode ? null : this.state.orderDraft })}
                        >
                            {reorderMode ? 'ביטול סידור' : 'שנה את סדר ההטבות'}
                        </Button>
                    }
                />
                <div style={mediwhoAppTableWrapStyle} className="mediwho-app-table-wrap">
                {reorderMode && (
                    <div style={{ margin: '16px 0 0', padding: '10px 16px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, direction: 'rtl' }}>
                        <span style={{ fontSize: 13, color: '#1d39c4' }}>גרור שורות לשינוי הסדר</span>
                        {orderDraft && (
                            <>
                                <Button size="small" type="primary" className="mediwho-benefits-save-order-btn" loading={reorderSaving} onClick={this.saveOrder}>שמור סדר</Button>
                                <Button size="small" className="mediwho-benefits-cancel-order-btn" onClick={() => this.setState({ orderDraft: null })}>בטל שינויים</Button>
                            </>
                        )}
                    </div>
                )}
                    <Table
                        rowKey="id"
                        loading={loading || reorderSaving}
                        dataSource={displayData}
                        columns={this.columns()}
                        onRow={reorderMode ? ((_record: any, index: number) => ({
                            draggable: true,
                            onDragStart: () => { this.dragIndex = index },
                            onDragOver: (e: React.DragEvent) => { e.preventDefault() },
                            onDrop: () => {
                                const from = this.dragIndex
                                if (from === index) return
                                const list: any[] = this.state.orderDraft || this.state.data
                                const next = this.swapRows(list, from, index)
                                if (next) this.setState({ orderDraft: next })
                            },
                            style: { cursor: 'grab' }
                        })) : undefined}
                        pagination={{
                            current: page,
                            total,
                            pageSize: 10,
                            onChange: (p: number) => this.load(p)
                        }}
                    />
                </div>
                <BenefitModal
                    visible={modalOpen}
                    benefit={selected}
                    activeCount={activeTotal}
                    inactiveCount={inactiveTotal}
                    isPartner={this.isPartner()}
                    takenByOthers={
                        this.isPartner()
                            ? (this.state.allRows || [])
                                  .filter((r: any) => !this.isOwn(r))
                                  .map((r: any) => r.sort_order)
                                  .filter((n: number) => !!n)
                            : []
                    }
                    onClose={this.closeModal}
                    onSaved={this.onSaved}
                />
                <NotificationModal
                    visible={notifModalOpen}
                    saving={notifSaving}
                    notification={this.state.notifPrefill}
                    onCancel={() => this.setState({ notifModalOpen: false })}
                    onSubmit={this.handleNotifSubmit}
                />
            </Layout>
        )
    }
}

export default BenefitsPage
